import {
  getEnhancedFixedAmountInPercent,
  getEnhancedOfferPrice,
  getEnhancedUpfrontFee,
} from 'components/common-form/sidePanel/priceEnhancer/PriceEnhancerRules';
import type { AddonManagementQuoteResultWithTradeWindowDetails } from 'epic/postTradeOperations/addonManagement/pricingConflationEpics';
import type { FormError } from 'models/formError';
import type { ListFieldMetadata } from 'models/forms/fields';
import {
  addonMergeDealsRequestedChanged,
  addonNotionalChanged,
  addonOfferPriceChanged,
  addonSolveModeChanged,
  addonUpFrontFeeChanged,
  addonXMarginChanged,
} from 'state/addonForm/addonForm.action';
import type { RemunerationMode, SolveMode } from 'state/addonForm/addonForm.common';
import type { AddonManagementPostTradeForm } from 'state/addonManagementForm/addonManagementForm.model';
import type { QuoteAction } from 'state/common-form/infos/infos.model';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import { formEnrichedDataReceived, formEnrichmentRequested } from 'state/formData/formData.action';
import type { DataById } from 'state/formData/formData.model';
import { checkFormEnrichmentRequestNeededThunk, formInputDataChangedThunk } from 'state/formData/formData.thunks';
import { quotesRemoved } from 'state/quotes/quotes.actions';
import {
  getAddonManagementFormFieldMetadata,
  getAddonManagementFormStatusById,
  getForm,
  getFormEnrichedData,
  getPrimaryTrade,
} from 'state/selectors';
import type { Thunk } from 'state/state.common';

import {
  addonDefaultingToggled,
  addonManagementFormDataChangedWithoutPatch,
  addonManagementFormEnrichmentReceived,
  addonManagementFormUncheckAllCheckbox,
  addonManagementQuoteAddToQueue,
  addonManagementQuoteReceived,
  addonManagementQuoteRemoved,
  addonManagementQuoteTradeRequested,
  addonTabCreatedFromAddonManagement,
} from './addonManagementForm.action';
import * as addonManagmentSelector from './addonManagementForm.selectors';
import {
  getAddonManagementFormIdByLastQuoteId,
  getAddonManagementFormLastQuoteId,
  getAddonManagementQuoteId,
  getAddonManagementQuoteResult,
  getSelectedFormIdsInAddonManagement,
} from './addonManagementForm.selectors';

export function selectedAddonManagementFormToQuoteThunk(): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const state = getState();
    const formIds = getSelectedFormIdsInAddonManagement(state);
    if (formIds.length > 0) {
      const formIdsToQuoteIds = Object.fromEntries(formIds.map(formId => [formId, getNewGuid()]));
      dispatch(addonManagementQuoteAddToQueue(formIdsToQuoteIds));
    }
  };
}

export function selectedAddonManagementFormToTradeThunk(): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const formIds = getSelectedFormIdsInAddonManagement(state);
    formIds.forEach(formId => {
      const lastQuoteId = getAddonManagementFormLastQuoteId(state, formId);
      if (lastQuoteId) {
        dispatch(addonManagementQuoteTradeRequested(formId, lastQuoteId));
      }
    });
  };
}

export function addonManagementQuoteTradeRequestedThunk(productId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const quoteId = addonManagmentSelector.getAddonManagementFormLastQuoteId(state, productId)!;
    dispatch(addonManagementQuoteTradeRequested(productId, quoteId));
  };
}

// TODO: unit test
export function addonManagementFormEnrichmentReceivedThunk(
  data: DataById,
  fieldsMetadata: FieldMetadataById,
  formId: string,
  actions: QuoteAction[],
  validationErrors?: FormError[],
): Thunk {
  return dispatch => {
    dispatch(addonManagementFormEnrichmentReceived(data, fieldsMetadata, formId, actions, validationErrors));
    dispatch(formEnrichedDataReceived('', formId, data, 'addonManagement'));
    dispatch(checkFormEnrichmentRequestNeededThunk('', formId, 'addonManagement', fieldsMetadata));
  };
}

export function addonManagementQuoteRemovedThunk(formIds: readonly string[]): Thunk<void> {
  return function (dispatch, getState) {
    const state = getState();

    const quoteIds = formIds.reduce<readonly string[]>((result, formId) => {
      const quoteId = getAddonManagementQuoteId(state, formId);
      return quoteId ? [...result, quoteId] : result;
    }, []);

    dispatch(addonManagementQuoteRemoved(formIds));
    dispatch(quotesRemoved(undefined, ...quoteIds));
  };
}

export function quoteResultReceivedThunk(
  quoteId: string,
  quoteResult: AddonManagementQuoteResultWithTradeWindowDetails,
): Thunk {
  return (dispatch, getState) => {
    const state = getState();

    const formId = getAddonManagementFormIdByLastQuoteId(state, quoteId);
    if (!formId) {
      return;
    }

    dispatch(addonManagementQuoteReceived(formId, quoteId, quoteResult));
  };
}

export function addonManagementChangeFormValueThunk(formId: string, key: string, value: unknown): Thunk {
  return (dispatch, getState) => {
    const state = getState();

    const status: string = getAddonManagementFormStatusById(state, formId);

    if (status === 'Quoted' && (key === 'TradingPrice' || key === 'XMargin')) {
      dispatch(addonManagementFormDataChangedWithoutPatch(formId, key, value as Readonly<unknown>));

      const formEnrichedData = getFormEnrichedData(state, formId);
      const quoteResult = getAddonManagementQuoteResult(state, formId);

      const issuePrice = (quoteResult?.priceEnhancement?.issuePrice ?? 0) * 100;
      const tradingOffer = Number(key === 'TradingPrice' ? (value ?? 0) : (formEnrichedData.TradingPrice ?? 0));
      const xMargin = Number(key === 'XMargin' ? (value ?? 0) : (formEnrichedData.XMargin ?? 0));

      const upfrontFee = getEnhancedUpfrontFee(issuePrice, tradingOffer, xMargin);
      const offerPrice = getEnhancedOfferPrice(tradingOffer, xMargin);
      const fixedAmountInPercent = getEnhancedFixedAmountInPercent(tradingOffer, xMargin);

      const remunerationMode = formEnrichedData.RemunerationMode;
      switch (remunerationMode) {
        case 'Upfront':
          dispatch(addonManagementFormDataChangedWithoutPatch(formId, 'UpfrontFee', upfrontFee as Readonly<unknown>));
          break;
        case 'Reoffer':
          dispatch(addonManagementFormDataChangedWithoutPatch(formId, 'OfferPrice', offerPrice as Readonly<unknown>));
          break;
        case 'Premium':
          dispatch(
            addonManagementFormDataChangedWithoutPatch(
              formId,
              'FixedAmountInPercent',
              fixedAmountInPercent as Readonly<unknown>,
            ),
          );
          break;
        default:
          break;
      }
    } else {
      dispatch(formInputDataChangedThunk(formId, formId, key, value as Readonly<unknown>));
    }
  };
}

export function addonManagementFormOpenPostTradeTabsThunk(): Thunk {
  return (dispatch, getState, { getNewGuid, navigateTo }) => {
    const state = getState();

    const formIds = getSelectedFormIdsInAddonManagement(state);

    const addonManagementPostTradeForms: AddonManagementPostTradeForm[] = getAddonManagementPostTradeForms(
      state,
      getNewGuid,
      formIds,
    );

    addonManagementPostTradeForms.forEach(({ formId, dataById, tabId, rfqId, tabName }) => {
      dispatch(addonDefaultingToggled(formId));
      dispatch(formEnrichmentRequested(formId, formId, dataById, 'addonManagement'));
      dispatch(addonTabCreatedFromAddonManagement(tabId, formId, rfqId, tabName));
    });

    dispatch(addonManagementFormUncheckAllCheckbox());
    navigateTo('/post-trade');
  };
}

function getAddonManagementPostTradeForms(
  state: any,
  getNewGuid: any,
  formIds: readonly string[],
): AddonManagementPostTradeForm[] {
  return formIds.map(formId => {
    const enrichedData = getFormEnrichedData(state, formId);
    const primaryTrade = getPrimaryTrade(state, formId);

    const remunerationMode = enrichedData?.remunerationMode as RemunerationMode;
    const metaData = getAddonManagementFormFieldMetadata(state, formId);
    const dealSplitMetaData = metaData['DealSplit'] as ListFieldMetadata;
    const availableCustodians = dealSplitMetaData.availableItems;
    const custodian = availableCustodians.length === 2 ? availableCustodians[1].value : availableCustodians[0].value;

    const defaultData: DataById = {
      NotionalAmount: primaryTrade?.notional as number,
      SolvingMode: getSolveModeByRemunerationMode(remunerationMode),
      DealSplit: custodian,
      XMargin: primaryTrade?.xMargin as number,
      UpfrontFee: primaryTrade?.upfrontFee as number,
      OfferPrice: primaryTrade?.offerPrice as number,
      FixedAmountInPercent: primaryTrade?.fixedAmount as number,
    };

    const rfqId = enrichedData?.QuoteId as string;
    const isin = enrichedData?.IsinCode as string;
    const fileCode = enrichedData?.FileCode as string;
    const tabName = getTabName(isin, fileCode, formId);

    const tabId = getNewGuid();

    const addonManagementPostTradeForm: AddonManagementPostTradeForm = {
      formId: formId,
      workspace: 'addonManagement',
      dataById: defaultData,
      tabId: tabId,
      rfqId: rfqId,
      tabName: tabName,
    };

    return addonManagementPostTradeForm;
  });
}

function getTabName(isin: string, fileCode: string, formId: string) {
  if (isin !== '') {
    return isin;
  }
  if (fileCode !== '') {
    return fileCode;
  }
  return formId;
}

function getSolveModeByRemunerationMode(remunerationMode: RemunerationMode): SolveMode {
  switch (remunerationMode) {
    case 'Upfront':
      return 'UpfrontFee';
    case 'Reoffer':
      return 'OfferPrice';
    case 'Premium':
      return 'FixedAmount';
    default:
      return 'UpfrontFee';
  }
}

export function addonFormDefaultingThunk(tabId: string, productId: string): Thunk {
  return (dispatch, getState) => {
    const state = getState();

    const formInfo = getForm(state, productId);
    const defaultingInAddOn = formInfo?.defaultingInAddOn;
    if (defaultingInAddOn) {
      dispatch(addonMergeDealsRequestedChanged(tabId, false));
      dispatch(addonDefaultingToggled(productId));

      const enrichedData = getFormEnrichedData(state, productId);
      const remunerationMode = enrichedData?.remunerationMode as RemunerationMode;

      const primaryTrade = getPrimaryTrade(state, productId);

      if (primaryTrade) {
        const solveMode = getSolveModeByRemunerationMode(remunerationMode);
        dispatch(addonSolveModeChanged(tabId, solveMode));

        dispatch(addonNotionalChanged(tabId, primaryTrade?.notional));

        if (solveMode === 'XMargin') {
          if (remunerationMode === 'Upfront') {
            dispatch(addonUpFrontFeeChanged(tabId, primaryTrade?.upfrontFee));
          }

          if (remunerationMode === 'Reoffer') {
            dispatch(addonOfferPriceChanged(tabId, primaryTrade?.offerPrice));
          }
        } else {
          dispatch(addonXMarginChanged(tabId, primaryTrade?.xMargin));
        }
      }
    }
  };
}
